/**
 * The iOS "Add to Home Screen" chip.
 *
 * iOS has no install API, so this cannot be a button that installs anything —
 * it can only point at Safari's share sheet and say which item to tap. That
 * makes restraint the whole design: it appears once the player has something to
 * lose (a save on disk), it is dismissable, and the dismissal is permanent.
 *
 * Never shown inside the installed app, and never on a platform where the
 * instructions would be wrong.
 */

import { isIosSafari, isStandalone } from '../durability';

const KEY_DISMISSED = 'rr.a2hs.dismissed';

function dismissed(): boolean {
  try {
    return localStorage.getItem(KEY_DISMISSED) === '1';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(KEY_DISMISSED, '1');
  } catch {
    /* the chip simply reappears next session */
  }
}

export interface A2hsChip {
  /** Re-evaluates whether the chip should be up. Cheap; call it after a save. */
  update(): void;
}

/**
 * @param hasSaves  whether a save exists yet. The chip is worthless before the
 *   first one and slightly insulting after a wipe.
 */
export function mountA2hs(hasSaves: () => boolean): A2hsChip {
  if (!isIosSafari() || isStandalone() || dismissed()) return { update: () => {} };

  const chip = document.createElement('div');
  chip.id = 'rr-a2hs';
  chip.hidden = true;

  const text = document.createElement('div');
  text.className = 'rr-a2hs-text';
  const title = document.createElement('strong');
  title.textContent = 'Add to Home Screen';
  const body = document.createElement('span');
  // Deliberately hedged. WebKit's storage policy is the sort of thing that
  // changes in a point release, and a promise we cannot keep is worse than the
  // nudge is worth: home-screen apps sit outside the seven-day tab cleanup
  // today, but "much less likely" is the claim we can stand behind next year.
  body.textContent = 'Safari clears storage for sites it only sees in a tab. An installed copy keeps its saves.';
  text.append(title, body);

  const how = document.createElement('button');
  how.className = 'tiny primary';
  how.textContent = 'How';

  const close = document.createElement('button');
  close.className = 'tiny ghost';
  close.textContent = 'Not now';
  close.addEventListener('click', () => {
    markDismissed();
    chip.remove();
    sheet.remove();
  });

  chip.append(text, how, close);

  const sheet = document.createElement('div');
  sheet.id = 'rr-a2hs-sheet';
  sheet.hidden = true;
  const panel = document.createElement('div');
  panel.className = 'rr-a2hs-panel';
  const heading = document.createElement('h3');
  heading.textContent = 'Add to Home Screen';
  const steps = document.createElement('ol');
  for (const step of [
    'Tap the Share button in Safari — the square with an arrow out of the top.',
    'Scroll down the list and tap "Add to Home Screen".',
    'Open Rerun Reactor from the icon from then on. Your game data and saves are already there.',
  ]) {
    const li = document.createElement('li');
    li.textContent = step;
    steps.appendChild(li);
  }
  const done = document.createElement('button');
  done.className = 'tiny';
  done.textContent = 'Close';
  const hide = () => {
    sheet.hidden = true;
  };
  done.addEventListener('click', hide);
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) hide();
  });
  panel.append(heading, steps, done);
  sheet.appendChild(panel);

  how.addEventListener('click', () => {
    sheet.hidden = false;
  });

  document.body.append(chip, sheet);

  const update = () => {
    if (!chip.isConnected) return;
    // A player who installs mid-session should not be nagged for the rest of it.
    if (isStandalone()) {
      chip.remove();
      sheet.remove();
      return;
    }
    chip.hidden = !hasSaves();
  };
  update();
  return { update };
}
